import { t } from "@lingui/macro";
import { currency, percent } from "@src/utils/formatters";
import cs from "classnames";
import React, { type FC } from "react";
import type { ProjectData } from "../../models";
import style from "./BudgetItem.module.css";

type TExpense = ProjectData["expensesStructured"][0]["budgetItems"][0];
type TOurWork = ProjectData["ourWorkStructured"][0]["budgetItems"][0];
type TBudgetItem = TExpense | TOurWork;

interface BudgetItemProps {
  item: TBudgetItem;
  amount: number;
}

export const BudgetItem: FC<React.PropsWithChildren<BudgetItemProps>> = ({
  item,
  amount,
}) => {
  return (
    <div className="flex pt-6">
      {/* 1. column */}
      <div className={cs("flex flex-col w-2/6", style.firstCol)}>
        <label className="font-semibold" htmlFor={item.id}>
          {item.title} ({currency.formatByWorkspace(item.stats.total_budget)})
        </label>
        {isExpense(item) ? (
          <React.Fragment>
            <p className="text-gray-600">
              {t`Invoiced`}: {currency.formatByWorkspace(item.stats.credit)}
            </p>
            <p className="text-gray-600">
              {t`Expenses`}:{" "}
              {currency.formatByWorkspace(item.stats.real_expenses)}
            </p>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <p className="text-gray-600">
              {t`Invoiced`}: {currency.formatByWorkspace(item.stats.credit)}
            </p>
            <p className="text-gray-600">
              {t`Uninvoiced`}:{" "}
              {currency.formatByWorkspace(item.stats.uninvoiced)}
            </p>
          </React.Fragment>
        )}
      </div>

      {/* 2. column */}
      <div className="flex flex-col w-1/6">
        {isExpense(item) && (
          <React.Fragment>
            <label
              className="text-gray-600 mb-4"
              htmlFor={item.id}
            >{t`Expenses`}</label>
            <p className="text-2xl font-semibold">
              {currency.formatByWorkspace(item.stats.planned_expenses)}
            </p>
          </React.Fragment>
        )}
      </div>

      {/* 3. column */}
      <div className="flex flex-col w-2/6">
        {isExpense(item) && (
          <React.Fragment>
            <label
              className="text-gray-600 mb-4"
              htmlFor={item.id}
            >{t`Commission`}</label>
            <p className="text-2xl font-semibold">
              {`${currency.formatByWorkspace(item.stats.planned_commission)} `}(
              {percent.format(item.stats.planned_commission_relative_computed)})
            </p>
            {!!item.completed_at && (
              <React.Fragment>
                <label
                  className="text-gray-600 mt-6"
                  htmlFor={item.id}
                >{t`Final commission`}</label>
                <p className="text-2xl font-semibold">
                  {`${currency.formatByWorkspace(item.stats.real_commission)} `}
                  ({percent.format(item.stats.real_commission_relative)})
                </p>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>

      {/* 4. column */}
      <div className="flex flex-col w-1/6">
        <label
          className="text-gray-600 mb-4"
          htmlFor={item.id}
        >{t`Amount`}</label>
        <p className="text-2xl font-semibold">
          {currency.formatByWorkspace(amount)}
        </p>
      </div>
    </div>
  );
};

function isExpense(item: TBudgetItem): item is TExpense {
  return (item as TExpense).stats.real_expenses !== undefined;
}
